const ENDPOINT_BASE = 'https://navigator-analytics.tweakwise.com/api';
const SESSION_COOKIE = 'tw_sid';
const PROFILE_COOKIE = 'tw_pid';
const SESSION_TTL_MINUTES = 30;
const PROFILE_TTL_MINUTES = 60 * 24 * 365 * 2;

export class TweakwiseEvents {

    private static instanceKey: string | null = null;

    public static bootstrap(instanceKey: string | null | undefined): void {
        if (!instanceKey || instanceKey === '~') {
            // Clear stale key so navigating from a configured page to an unconfigured one stops tracking for the prior tenant.
            this.instanceKey = null;
            return;
        }
        this.instanceKey = instanceKey;
        // Detect a fresh session directly from the cookie (missing = new session or expired), so tw_sid renewal after 30 min inactivity re-emits sessionstart.
        const hadSession = this.getCookie(SESSION_COOKIE) !== null;
        const sessionKey = this.getOrCreateSessionKey();
        const profileKey = this.getOrCreateProfileKey();
        if (!hadSession) {
            this.send('sessionstart', {
                SessionKey: sessionKey,
                ProfileKey: profileKey,
                Source: 'web',
            });
        }
    }

    public static search(query: string): void {
        if (!this.instanceKey || !query) {
            return;
        }
        this.send('search', {
            SessionKey: this.getOrCreateSessionKey(),
            ProfileKey: this.getOrCreateProfileKey(),
            SearchTerm: query,
        });
    }

    public static pageImpression(requestId: string | null): void {
        if (!this.instanceKey) {
            return;
        }
        const sessionKey = this.getOrCreateSessionKey();
        // Our search proxy does not expose Tweakwise's twn-request-id response header; fall back to the sessionKey so the event still fires.
        this.send('pageimpression', {
            SessionKey: sessionKey,
            RequestId: requestId ?? sessionKey,
        });
    }

    public static itemClick(requestId: string | null, itemId: string | number): void {
        if (!this.instanceKey || !itemId) {
            return;
        }
        const sessionKey = this.getOrCreateSessionKey();
        this.send('itemclick', {
            SessionKey: sessionKey,
            ProfileKey: this.getOrCreateProfileKey(),
            RequestId: requestId ?? sessionKey,
            ItemId: String(itemId),
        });
    }

    private static send(event: string, payload: Record<string, unknown>): void {
        if (!this.instanceKey) {
            return;
        }
        const url = `${ENDPOINT_BASE}/${event}?instanceKey=${encodeURIComponent(this.instanceKey)}`;
        try {
            const body = JSON.stringify(payload);
            // text/plain is CORS-safelisted so sendBeacon skips preflight; Tweakwise parses the JSON body regardless of Content-Type.
            if (typeof navigator.sendBeacon === 'function') {
                const blob = new Blob([body], { type: 'text/plain' });
                if (navigator.sendBeacon(url, blob)) {
                    return;
                }
            }
            fetch(url, {
                method: 'POST',
                body,
                headers: { 'Content-Type': 'text/plain' },
                keepalive: true,
                mode: 'no-cors',
            }).catch(() => { /* fire-and-forget */ });
        } catch {
            /* telemetry never breaks UX */
        }
    }

    private static getOrCreateSessionKey(): string {
        let key = this.getCookie(SESSION_COOKIE);
        if (!key) {
            key = this.uuid();
        }
        this.setCookie(SESSION_COOKIE, key, SESSION_TTL_MINUTES);
        return key;
    }

    private static getOrCreateProfileKey(): string {
        let key = this.getCookie(PROFILE_COOKIE);
        if (!key) {
            key = this.uuid();
            this.setCookie(PROFILE_COOKIE, key, PROFILE_TTL_MINUTES);
        }
        return key;
    }

    private static getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    }

    private static setCookie(name: string, value: string, ttlMinutes: number): void {
        const expires = new Date(Date.now() + ttlMinutes * 60 * 1000).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
    }

    private static uuid(): string {
        const c = (window as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

}
